import { create } from "zustand";
import { discoverCloudAccount, getDiscoveryRun } from "../api/frameworksApi";
import { useToastStore } from "./toastStore";

export interface ActiveDiscoveryRun {
  runId: string;
  cloudProviderId: string;
  accountName: string;
  status: "RUNNING" | "SUCCESS" | "FAILED";
  progressPercent: number;
  displayedPct: number;
  regionsCompleted: number;
  regionsTotal: number;
  errorMessage: string | null;
  totalDiscovered: number;
}

interface DiscoveryState {
  runs: Record<string, ActiveDiscoveryRun>; // key = cloudProviderId
  startDiscovery: (cloudProviderId: string, accountName: string) => Promise<void>;
  dismiss: (cloudProviderId: string) => void;
}

// Module-level map for interval IDs — not serializable, kept outside Zustand state
const _intervals = new Map<string, ReturnType<typeof setInterval>>();

function stopPolling(cloudProviderId: string) {
  const id = _intervals.get(cloudProviderId);
  if (id !== undefined) {
    clearInterval(id);
    _intervals.delete(cloudProviderId);
  }
}

export const useDiscoveryStore = create<DiscoveryState>((set, get) => ({
  runs: {},

  startDiscovery: async (cloudProviderId, accountName) => {
    const existing = get().runs[cloudProviderId];
    if (existing?.status === "RUNNING") {
      useToastStore.getState().showError("Discovery", "A discovery is already running for this account");
      return;
    }

    try {
      const res = await discoverCloudAccount(cloudProviderId);
      const runId = res.data?.runId;
      if (!runId) {
        useToastStore.getState().showError("Discovery", "Failed to start discovery — no run ID returned");
        return;
      }

      useToastStore.getState().showSuccess(
        "Discovery started",
        res.data?.message || "Resources will appear shortly"
      );

      set((state) => ({
        runs: {
          ...state.runs,
          [cloudProviderId]: {
            runId,
            cloudProviderId,
            accountName,
            status: "RUNNING",
            progressPercent: 0,
            displayedPct: 0,
            regionsCompleted: 0,
            regionsTotal: 0,
            errorMessage: null,
            totalDiscovered: 0,
          },
        },
      }));

      let elapsed = 0;
      const intervalId = setInterval(async () => {
        elapsed += 2000;
        // 2-min cap
        if (elapsed > 120_000) {
          stopPolling(cloudProviderId);
          set((state) => ({
            runs: {
              ...state.runs,
              [cloudProviderId]: { ...state.runs[cloudProviderId], status: "FAILED", errorMessage: "Timed out — still running in background" },
            },
          }));
          return;
        }

        try {
          const run = await getDiscoveryRun(runId);
          const d = run.data;
          if (!d) return;

          const prevDisplayedPct = get().runs[cloudProviderId]?.displayedPct ?? 0;
          const serverPct = d.progressPercent ?? 0;
          const newDisplayedPct = Math.max(prevDisplayedPct, serverPct);

          set((state) => ({
            runs: {
              ...state.runs,
              [cloudProviderId]: {
                ...state.runs[cloudProviderId],
                status: d.status,
                progressPercent: serverPct,
                displayedPct: d.status === "SUCCESS" ? 100 : newDisplayedPct,
                regionsCompleted: d.regionsCompleted ?? 0,
                regionsTotal: d.regionsTotal ?? 0,
                totalDiscovered: d.totalDiscovered ?? 0,
                errorMessage: d.errorMessage ?? null,
              },
            },
          }));

          if (d.status === "SUCCESS") {
            stopPolling(cloudProviderId);
            useToastStore.getState().showSuccess(
              "Discovery complete",
              `Discovered ${d.totalDiscovered} resources across ${d.regionsScanned?.length ?? d.regionsCompleted ?? 0} regions`
            );
          } else if (d.status === "FAILED") {
            stopPolling(cloudProviderId);
            useToastStore.getState().showError("Discovery failed", d.errorMessage || "Discovery failed");
          }
        } catch {
          // keep polling on transient errors
        }
      }, 2000);

      _intervals.set(cloudProviderId, intervalId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to start discovery";
      useToastStore.getState().showError("Discovery error", msg);
    }
  },

  dismiss: (cloudProviderId) => {
    stopPolling(cloudProviderId);
    set((state) => {
      const next = { ...state.runs };
      delete next[cloudProviderId];
      return { runs: next };
    });
  },
}));
